/**
 * Maps human-readable keys to translation keys in the howItWorks namespace.
 * Used by the how-it-works page to look up translations from messages/{locale}.json.
 *
 * Keys are sourced from multiple XLIFF namespaces (locations, home, faq, insurance,
 * imprint, report, contact, cta) and consolidated into a single howItWorks namespace.
 */
export const howItWorksKeys = {
  // Hero
  heroLabel: "heroLabel",
  heroHeading: "heroHeading",
  heroSubtitle: "heroSubtitle",
  heroBodyText: "heroBodyText",

  // Value cards
  valueCard1Label: "valueCard1Label",
  valueCard1Title: "valueCard1Title",
  valueCard1Desc: "valueCard1Desc",
  valueCard2Label: "valueCard2Label",
  valueCard2Title: "valueCard2Title",
  valueCard2Desc: "valueCard2Desc",
  valueCard3Label: "valueCard3Label",
  valueCard3Title: "valueCard3Title",
  valueCard3Desc: "valueCard3Desc",

  // Health risks marquee
  healthRisksHeading: "healthRisksHeading",
  healthRisksSubtitle: "healthRisksSubtitle",
  healthRisksLink: "healthRisksLink",
  heartDiseaseRisk: "heartDiseaseRisk",
  diabetes: "diabetes",
  fattyLiver: "fattyLiver",
  spinalDysfunction: "spinalDysfunction",
  endometriosis: "endometriosis",
  liverTumors: "liverTumors",
  metabolicSyndrome: "metabolicSyndrome",
  immuneDysregulation: "immuneDysregulation",
  benignHyperplasia: "benignHyperplasia",
  dementia: "dementia",
  brainAneurysms: "brainAneurysms",
  silentStrokes: "silentStrokes",
  hemochromatosis: "hemochromatosis",
  neurodegeneration: "neurodegeneration",
  nerveCompression: "nerveCompression",
  prostateCancer: "prostateCancer",
  arteriosclerosis: "arteriosclerosis",
  structuralIssues: "structuralIssues",

  // Blood-detectable risks — the Germany marquee (see the `bloodOnly`
  // branch in how-it-works-client.tsx). Every one of these is a condition
  // /what-we-detect already lists under the blood check-up, so the DE page
  // can name them without the scan it doesn't sell.
  prediabetes: "prediabetes",
  insulinResistance: "insulinResistance",
  dyslipidemia: "dyslipidemia",
  highCholesterol: "highCholesterol",
  liverFibrosis: "liverFibrosis",
  hypothyroidism: "hypothyroidism",
  hashimoto: "hashimoto",
  ironDeficiency: "ironDeficiency",
  anemia: "anemia",
  vitaminDDeficiency: "vitaminDDeficiency",
  vitaminB12Deficiency: "vitaminB12Deficiency",
  kidneyDisease: "kidneyDisease",

  // Steps
  stepsHeading: "stepsHeading",
  stepsSubtitle: "stepsSubtitle",
  step1Label: "step1Label",
  step1Title: "step1Title",
  step1Desc: "step1Desc",
  step2Label: "step2Label",
  step2Title: "step2Title",
  step2Desc: "step2Desc",
  step3Label: "step3Label",
  step3Title: "step3Title",
  step3Desc: "step3Desc",
  step4Label: "step4Label",
  step4Title: "step4Title",
  step4Desc: "step4Desc",

  // Products section
  productsHeading: "productsHeading",
  productsSubtitle: "productsSubtitle",
  talkToTeam: "talkToTeam",

  // Trust section
  trustSectionHeading: "trustSectionHeading",

  // Trust cards
  trustCard1Title: "trustCard1Title",
  trustCard1Desc: "trustCard1Desc",
  trustCard2Title: "trustCard2Title",
  trustCard2Desc: "trustCard2Desc",
  trustCard3Title: "trustCard3Title",
  trustCard3Desc: "trustCard3Desc",
  trustCard4Title: "trustCard4Title",
  trustCard4Desc: "trustCard4Desc",

  // Pricing preview
  pricingHeading: "pricingHeading",
  pricingProp1: "pricingProp1",
  pricingProp2: "pricingProp2",
  pricingProp3: "pricingProp3",
  pricingProp4: "pricingProp4",
  pricingTabMri: "pricingTabMri",
  pricingTabBlood: "pricingTabBlood",
  // 3rd pricing tab (matches /pricing). Shown in both
  // arms; the pricing tabs no longer vary by experiment.
  pricingTabMembership: "pricingTabMembership",
  pricingMriTitle: "pricingMriTitle",
  pricingMriDuration: "pricingMriDuration",
  pricingMriNoRadiation: "pricingMriNoRadiation",
  pricingMriDesc: "pricingMriDesc",
  pricingBloodTitle: "pricingBloodTitle",
  pricingBloodDuration: "pricingBloodDuration",
  pricingBloodDesc: "pricingBloodDesc",
  bookCheckup: "bookCheckup",
  viewAllPackages: "viewAllPackages",
  chf: "chf",
  priceMri: "priceMri",
  priceBlood: "priceBlood",

  // Insurance section
  insuranceHeading: "insuranceHeading",
  insuranceSubtitle: "insuranceSubtitle",
  insuranceKptTitle: "insuranceKptTitle",
  insuranceKptDesc: "insuranceKptDesc",

  // Testimonials
  testimonialsHeading: "testimonialsHeading",

  // FAQ
  faqHeading: "faqHeading",

  // Bottom CTA
  ctaHeading: "ctaHeading",

  // Banner
  bannerMessage: "bannerMessage",
  joinWaitlist: "joinWaitlist",

  // Footer
  footerTagline: "footerTagline",
  termsConditions: "termsConditions",
  privacyPolicy: "privacyPolicy",
  imprintLink: "imprintLink",
  copyright: "copyright",

  // Package cards — MRI tiers
  pkgCoreDesc: "pkgCoreDesc",
  pkgAdvancedDesc: "pkgAdvancedDesc",
  pkgProDesc: "pkgProDesc",
  bookCore: "bookCore",
  bookAdvanced: "bookAdvanced",
  bookPro: "bookPro",
  // "Includes" label variants (core / advanced / pro)
  includesLabel: "includesLabel",
  includesAdvancedLabel: "includesAdvancedLabel",
  includesProLabel: "includesProLabel",
  // Duration row inside package card
  mriDurationInfo: "mriDurationInfo",

  // Package feature names — Core
  featureHighResMri: "featureHighResMri",
  featureBrainMri: "featureBrainMri",
  featureSpineMri: "featureSpineMri",
  // Package feature names — Advanced
  featureAdvancedBloodTest: "featureAdvancedBloodTest",
  featureBodyComposition: "featureBodyComposition",
  featureProstateOvariesMri: "featureProstateOvariesMri",
  // Package feature names — Pro
  featureBrainVolumetry: "featureBrainVolumetry",
  featureHipsKneesMri: "featureHipsKneesMri",
  featureHormonesVitamins: "featureHormonesVitamins",

  // Trust signals (inside package cards)
  trustReviewedBySwiss: "trustReviewedBySwiss",
  trustPersonalActionPlan: "trustPersonalActionPlan",
  trustVideoConsultDoctor: "trustVideoConsultDoctor",

  // Comparison grid section labels
  comparisonImagingLabel: "comparisonImagingLabel",
  comparisonBloodLabel: "comparisonBloodLabel",

  // Comparison grid — imaging features
  imgHead: "imgHead",
  imgSpine: "imgSpine",
  imgNeck: "imgNeck",
  imgPelvis: "imgPelvis",
  imgAbdomen: "imgAbdomen",
  imgSpineMuscle: "imgSpineMuscle",
  imgProstateOvaries: "imgProstateOvaries",
  imgBodyComp: "imgBodyComp",
  imgBrainAnalysis: "imgBrainAnalysis",
  imgHipKnee: "imgHipKnee",

  // Comparison grid — blood features
  bloodInterpretExisting: "bloodInterpretExisting",
  bloodAdvancedTest: "bloodAdvancedTest",
  bloodHormoneTest: "bloodHormoneTest",
  bloodBVitaminsTest: "bloodBVitaminsTest",

  // Blood tab — standalone card
  bloodTabIncludesLabel: "bloodTabIncludesLabel",
  bloodFeature1: "bloodFeature1",
  bloodFeature2: "bloodFeature2",
  bloodFeature3: "bloodFeature3",
  bloodFeature4: "bloodFeature4",
  bloodFeature5: "bloodFeature5",
  bloodFeature6: "bloodFeature6",

  // Blood add-ons
  addonBVitaminsTitle: "addonBVitaminsTitle",
  addonBVitaminsDesc: "addonBVitaminsDesc",
  addonOmegaTitle: "addonOmegaTitle",
  addonOmegaDesc: "addonOmegaDesc",
  addonHormoneWomenTitle: "addonHormoneWomenTitle",
  addonHormoneWomenDesc: "addonHormoneWomenDesc",
  addonHormoneMenTitle: "addonHormoneMenTitle",
  addonHormoneMenDesc: "addonHormoneMenDesc",
} as const
